import {
	BaseListViewStore,
	UsernameCellData,
	BooleanToggleCellData,
	DateTimeCellData,
	PasswordExpirationCellData,
} from 'core/stores';
import { observable, action, reaction, makeObservable } from 'mobx';
import { ApplicationStore } from 'platform/application/stores';
import { UserStore } from 'modules/user/stores';
import { ModalParams, ConfirmModalParams } from 'core/models';
import { checkPermissions } from 'core/stores';
import { roles as allRoles, sellerRoles, warehouseRoles } from 'common/constants';
import { checkForColumnWidth, isSuperAdminOrganization, isWarehouse } from 'core/utils';
import { localStorageHandler } from 'core/services';
import { CoreUserInfoStore } from 'modules/core-user-info/stores';
import _ from 'lodash';

class UserListViewStore extends BaseListViewStore {
	@observable selectedItem = null;
	@observable selectedSeller = null;
	@observable sellerOptions = [];
	isSuperAdmin = false;
	showActions = true;
	localStorageWidthKey = 'users-column-width';

	constructor(rootStore) {
		super(rootStore, {
			listStore: new UserStore(rootStore),
			queryParams: {
				embed: [
					'organization',
					'coreUser',
					'coreUser.usersInRoles',
					'coreUser.usersInRoles.role',
					'coreUser.membership',
				],
			},
			tableData: {
				columns: [
					{
						key: 'coreUser.displayName',
						title: 'LOGINFORM.USERNAME',
						dataCell: UsernameCellData,
						sortable: true,
						selected: true,
					},
					{
						key: 'organization.applicationTitle',
						title: 'LOGINFORM.ORGANIZATION',
						sortable: true,
						selected: true,
						enabled: isSuperAdminOrganization(rootStore),
					},
					{
						key: 'coreUser.membership.email',
						sortable: true,
						title: 'LOGINFORM.EMAIL',
						selected: true,
					},
					{
						key: 'coreUserInfo.userPhone',
						title: 'LOGINFORM.USERPHONE',
						sortable: false,
					},
					{
						key: 'coreUser.membership.isApproved',
						title: 'LOGINFORM.APPROVED',
						sortable: false,
						onClick: user => this.userApprovalToggle(user),
						dataCell: BooleanToggleCellData,
						selected: true,
					},
					{
						key: 'coreUser.membership.isLockedOut',
						title: 'LOGINFORM.LOCKED',
						onClick: user => this.userLockToggle(user),
						sortable: false,
						dataCell: BooleanToggleCellData,
						selected: true,
					},
					{
						key: 'userRoles',
						sortable: false,
						title: 'LOGINFORM.USERROLES.LABEL',
						selected: true,
					},
					{
						key: 'coreUser.membership.createDate',
						title: 'LOGINFORM.USERCREATION',
						dataCell: DateTimeCellData,
						dateFormat: 'LLL',
						useLocalTime: true,
						selected: true,
						sortable: true,
					},
					{
						key: 'coreUser.membership.lastPasswordChangedDate',
						title: 'LOGINFORM.PASSWORDEXPIRATIONDAYS',
						dataCell: PasswordExpirationCellData,
						dateFormat: 'L',
						useLocalTime: true,
						selected: true,
						sortable: true,
					},
					{
						key: 'coreUser.membership.lastLoginDate',
						title: 'LOGINFORM.USERLASTACTIVITY',
						dataCell: DateTimeCellData,
						dateFormat: 'LLL',
						useLocalTime: true,
						selected: true,
						sortable: true,
					},
				],
				actions: {
					onUserEdit: userInOrgData => this.routes.edit(userInOrgData.coreUserId),
					onPasswordChange: userInOrgData => this.routes.passwordChange(userInOrgData.coreUserId),
					onDelete: async userInOrgData => this.openDeleteConfirmModal(userInOrgData),
				},
				sortObject: [
					{
						field: 'coreUser.displayName',
						dir: 'asc',
					},
				],
			},
			routes: {
				detail: id => this.rootStore.routerStore.goTo('master.app.main.user.preview', { id }),
				edit: id => this.rootStore.routerStore.setQueryParams({ editId: id }),
				passwordChange: id =>
					this.rootStore.routerStore.setQueryParams({
						changePassword_id: id,
					}),
				create: () => this.rootStore.routerStore.setQueryParams({ create: null }),
			},
			permissions: {
				update: () => this.authStore.hasPermission('coreUserSection.update'),
				create: () => this.authStore.hasPermission('coreUserSection.create'),
				remove: () => this.authStore.hasPermission('coreUserSection.delete'),
				read: () => this.authStore.hasPermission('coreUserSection.read'),
				full: () => this.authStore.hasPermission('coreUserSection.full'),
			},
		});
		makeObservable(this);
		if (isSuperAdminOrganization(rootStore)) {
			this.isSuperAdmin = true;
			this.fetchSellers();
			this.rootStore.viewStore.displaySellerDropdown = false;
		}
		if (isWarehouse(rootStore)) {
			this.rootStore.viewStore.handleSellerForChildViewStore(this);
		}
		this.queryUtility.initialize(this.findUsers);
		this.showActions = checkPermissions(this).actionsAllowed;
		reaction(
			() => this.rootStore.routerStore.routerState,
			state => {
				if (state.routeName !== 'master.app.main.user.list') {
					return;
				}

				const { editId, changePassword_id, create } = state.queryParams;

				this.editModal.setOpen(editId !== undefined);
				this.createModal.setOpen(create !== undefined);
				this.passwordChangeModal.setOpen(changePassword_id !== undefined);
			}
		);
		checkForColumnWidth(localStorageHandler.get(this.localStorageWidthKey), this.tableData.columns);
		this.setHeaderModes();
		this.coreUserInfoStore = new CoreUserInfoStore(rootStore);
	}

	@action.bound
	onSellerChange = item => {
		localStorageHandler.set('selectedSeller', item);
		this.selectedSeller = item;
		if (item == null) {
			this.queryUtility.filter.organizationIds = [];
		} else {
			this.queryUtility.filter.organizationIds = [item.value];
		}
		this.findUsers();
	};

	@action.bound
	fetchSellers = async () => {
		const applicationStore = new ApplicationStore(this.rootStore);
		const seller = localStorageHandler.get('selectedSeller');
		if (seller == null) {
			this.queryUtility.filter.organizationIds = [];
		} else {
			this.queryUtility.filter.organizationIds = [seller.value];
		}
		this.selectedSeller = seller;
		const data = await applicationStore.find({
			rpp: 50,
			page: 1,
			embed: ['organizationType'],
		});
		this.sellerOptions = _.map(data.item, item => {
			return { label: item.applicationTitle, value: item.id, extra: item };
		});
	};

	@action.bound
	openDeleteConfirmModal = userInOrgData => {
		this.selectedItem = userInOrgData;
		this.deleteConfirmModal.open({
			message: ['LOOKUPDELETECONFIRM.USER', { name: userInOrgData.coreUser.displayName.split('-')[0] }],
		});
	};

	@action.bound
	deleteUser = async user => {
		this.suspend();
		try {
			await this.listStore.deleteUser(user.id);
			this.rootStore.notificationStore.success(['USERVIEW.DELETE', { user: user.coreUser.displayName.split('-')[0] }]);
			await this.findUsers();
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	closeModal = () => {
		const params = this.queryUtility.getFilterUrlParams();
		this.rootStore.routerStore.updateQueryParams({
			editId: undefined,
			changePassword_id: undefined,
			create: undefined,
			...params,
		});
	};

	@action.bound
	findUsers = async () => {
		this.suspend();
		await this.listStore.findUsers(this.queryUtility.filter);
		let responseData = this.listStore.response;
		let userIds = [];
		_.forEach(responseData.item, o => {
			userIds.push(o.coreUserId);
		});
		let userInfoFilter = {
			ids: userIds,
			page: this.queryUtility.filter.pageNumber,
			rpp: this.queryUtility.filter.pageSize,
		};
		await this.coreUserInfoStore.findCoreUserInfo(userInfoFilter);
		let responseUserInfo = this.coreUserInfoStore.response;
		_.forEach(responseData.item, o => {
			o.coreUserInfo = _.filter(responseUserInfo.item, i => {
				return i.coreUserId === o.coreUserId;
			})[0];
		});
		this.queryUtility.handleResponse(responseData);
		this.resume();
	};

	@action.bound
	userLockToggle = async user => {
		let lock;
		// Do not allow user to lock him/herself out
		if (this.rootStore.authStore.user.id === user.id) return;
		this.suspend();
		user.coreUser.id = user.coreUserId;
		if (user.coreUser.membership.isLockedOut) {
			await this.listStore.unlockUser(user.coreUser);
			lock = 'USEREDIT.LOCKING.UNDONE';
		} else {
			await this.listStore.lockUser(user.coreUser);
			lock = 'USEREDIT.LOCKING.DONE';
		}
		this.rootStore.notificationStore.success(['USEREDIT.LOCKING.MESSAGE', { lock: lock, user: user.userName }]);
		await this.findUsers();
		this.resume();
	};

	@action.bound
	userApprovalToggle = async user => {
		let approve;
		// Do not allow user to disapprove him/herself
		if (this.rootStore.authStore.user.id === user.id) return;
		this.suspend();
		user.coreUser.id = user.coreUserId;
		if (user.coreUser.membership.isApproved) {
			await this.listStore.disapproveUser(user.coreUser);
			approve = 'USEREDIT.APPROVING.UNDONE';
		} else {
			await this.listStore.approveUser(user.coreUser);
			approve = 'USEREDIT.APPROVING.DONE';
		}
		this.rootStore.notificationStore.success(['USEREDIT.APPROVING.MESSAGE', { approve: approve, user: user.userName }]);
		await this.findUsers();
		this.resume();
	};

	editModal = new ModalParams({
		onClose: this.closeModal,
	});

	passwordChangeModal = new ModalParams({
		onClose: this.closeModal,
	});

	createModal = new ModalParams({
		onClose: this.closeModal,
	});

	deleteModal = new ModalParams({
		onClose: this.closeModal,
	});

	deleteConfirmModal = new ConfirmModalParams({
		onConfirm: async () => {
			if (this.selectedItem != null) {
				this.deleteUser(this.selectedItem);
			}
		},
	});
}

export default UserListViewStore;



// WEBPACK FOOTER //
// ./src/modules/user/stores/UserListViewStore.js